import {
    useMutation, useQuery,
} from '@tanstack/react-query';
import { Flex, Space, TableColumnsType, Typography } from 'antd';
import {
    useEffect, useMemo, useState,
} from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';

import {
    objectionsMutations, objectionsQueries,
} from '@/api';
import {
    ActionDeleteButton,
    ActionEditButton,
    ActionsButtonWrapper,
    ActionViewButton,
    CreateButton,
    DefaultTable,
    DeleteProjectModal,
    RoleGuard,
    ThesesBadge,
} from '@/components';
import {
    AppRoute, DEFAULT_ORDER_COLUMN, DEFAULT_ORDER_DIRECTION, UserRole,
} from '@/constants';
import { useCustomParams } from '@/hooks';
import {
    DefaultOrderColumn, Objection,
    SortDirection,
} from '@/types';
import {
    getActionsColumn, getCreatedAtColumn,
    getUpdatedAtColumn,
} from '@/utils';
import { hasRole } from '@/utils/users.utils';
import { useUserStore } from '@/stores';

export const ObjectionsPage = () => {
    const navigate = useNavigate();
    const { user } = useUserStore();
    const { orderColumn, orderDirection, currentPage, pageSize, search, setOrderColumn, setOrderDirection } = useCustomParams();
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [objectionToDelete, setObjectionToDelete] = useState<Objection | null>(null);

    useEffect(() => {
        if (!orderColumn || orderColumn === 'id' || orderColumn === DEFAULT_ORDER_COLUMN) {
            setOrderColumn('priority');
        }
        if (!orderDirection || orderDirection === DEFAULT_ORDER_DIRECTION) {
            setOrderDirection('asc');
        }
    }, [orderColumn, orderDirection, setOrderColumn, setOrderDirection]);

    const queryParams = useMemo(() => {
        const finalOrderColumn = (orderColumn && orderColumn !== 'id' && orderColumn !== DEFAULT_ORDER_COLUMN)
            ? orderColumn
            : 'priority';
        const finalOrderDirection = orderDirection || 'asc';

        return {
            per_page: pageSize,
            current_page: currentPage,
            order_column: finalOrderColumn as DefaultOrderColumn,
            order_direction: finalOrderDirection as SortDirection,
            search: search,
        };
    }, [pageSize, currentPage, orderColumn, orderDirection, search]);

    const { data, isFetching } = useQuery(objectionsQueries.getAllObjections(queryParams));

    const deleteMutationOptions = useMemo(
        () => objectionToDelete
            ? objectionsMutations.delete(objectionToDelete.id)
            : objectionsMutations.delete(''),
        [objectionToDelete],
    );

    const { mutate: deleteObjection, isPending: isDeleting } = useMutation(deleteMutationOptions);

    const handleDeleteConfirm = () => {
        if (objectionToDelete) {
            deleteObjection(undefined, {
                onSuccess: () => {
                    setDeleteModalOpen(false);
                    setObjectionToDelete(null);
                },
            });
        }
    };

    const handleDeleteCancel = () => {
        setDeleteModalOpen(false);
        setObjectionToDelete(null);
    };

    const columns: TableColumnsType<Objection> = useMemo(() => [
        {
            title: 'Назва',
            dataIndex: 'title',
            key: 'title',
            width: 300,
            sorter: true,
            sortDirections: ['ascend', 'descend', 'ascend'] as const,
            render: (title: string, record: Objection) => (
                <Flex vertical gap={4}>
                    <Space size="small" align="center">
                        <Typography.Link
                            onClick={() => navigate(`${AppRoute.Objections}/${record.id}`)}
                            style={{
                                display: "flex",
                                gap: "10px"
                            }}
                        >

                            <span>
                                🔷
                            </span>
                            <span>{title}</span>
                        </Typography.Link>
                    </Space>
                    {/*<ThesesBadge content={record.body} />*/}
                </Flex>
            ),
        },
        getUpdatedAtColumn(),
        getActionsColumn({
            width: hasRole(user, [UserRole.Admin, UserRole.SuperAdmin]) ? undefined : 80,
            render: (_: unknown, record: Objection) => {
                const handleDelete = () => {
                    setObjectionToDelete(record);
                    setDeleteModalOpen(true);
                };

                return (
                    <ActionsButtonWrapper>
                        <ActionViewButton
                            onClick={() => navigate(`${AppRoute.Objections}/${record.id}`)}
                            to={`${AppRoute.Objections}/${record.id}`}
                            text="Переглянути"
                        />
                        <RoleGuard roles={[UserRole.SuperAdmin, UserRole.Admin, UserRole.ScriptWriter]}>
                            <ActionsButtonWrapper>
                                <ActionEditButton
                                    onClick={() => navigate(`${AppRoute.Objections}/${record.id}/edit`)}
                                    to={`${AppRoute.Objections}/${record.id}/edit`}
                                />
                            </ActionsButtonWrapper>
                        </RoleGuard>
                        <RoleGuard roles={[UserRole.SuperAdmin, UserRole.Admin]}>
                            <ActionsButtonWrapper>
                                <ActionDeleteButton onClick={handleDelete} />
                            </ActionsButtonWrapper>
                        </RoleGuard>
                    </ActionsButtonWrapper>
                );
            },
        }),
    ], [navigate]);

    return (
        <>
            <Helmet>
                <title>Робота з запереченнями</title>
            </Helmet>

            <DefaultTable
                title="Робота з запереченнями"
                columns={columns}
                dataSource={data?.items || []}
                total={data?.total || 0}
                loading={isFetching}
                renderCreateButton={() => (
                    <RoleGuard roles={[UserRole.SuperAdmin, UserRole.Admin, UserRole.ScriptWriter]}>
                        <CreateButton
                            label="Створити заперечення"
                            disabled={isFetching}
                            to={AppRoute.ObjectionCreate}
                        />
                    </RoleGuard>
                )}
            />

            <DeleteProjectModal
                open={deleteModalOpen}
                entityName={objectionToDelete?.title}
                entityType="objection"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                loading={isDeleting}
            />
        </>
    );
};
